import { HelpCircleIcon, PlusIcon, SearchIcon } from "lucide-react";
import { useRef, useState } from "react";
import type {
  CmmdBoardTask,
  CmmdProjectAgentInstance,
  CmmdProjectAgentMember,
  CmmdProjectAgentMemberConsolidationPreview,
  CmmdProjectAgentMemberConsolidationResult,
  CmmdProjectLeadEvents,
  CreateCmmdProjectAgentMemberParams,
} from "@cmmd-forge/contracts";

import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { AgentKindBadge, AgentMemberAvatar } from "~/components/identity/AgentMemberIdentity";
import { cn } from "~/lib/utils";
import { flattenTaskTree } from "../tasks/taskViewTypes";
import { CreateAgentMemberDialog } from "./CreateAgentMemberDialog";
import { AgentMemberDetails } from "./ProjectAgentMemberDialogs";
import { AgentMemberConsolidationPanel } from "./AgentMemberConsolidationPanel";
import { formatTeamRole } from "./projectTeamLabels";

type CreateAgentInput = Omit<CreateCmmdProjectAgentMemberParams, "idempotencyKey">;

export interface ProjectAgentMembersProps {
  readonly projectId: string;
  readonly members: readonly CmmdProjectAgentMember[];
  readonly loading: boolean;
  readonly tasks: readonly CmmdBoardTask[];
  readonly error: string | null;
  readonly events: CmmdProjectLeadEvents["events"];
  readonly eventsLoading: boolean;
  readonly eventsError: string | null;
  readonly onCreate: (input: CreateAgentInput) => Promise<void>;
  readonly onRename: (memberId: number, displayName: string) => Promise<void>;
  readonly onRetire: (memberId: number) => Promise<void>;
  readonly onUpdateAvatar: (
    memberId: number,
    avatar: { readonly kind: "generated"; readonly seed: string } | { readonly kind: "default" },
  ) => Promise<void>;
  readonly onListInstances: (memberId: number) => Promise<ReadonlyArray<CmmdProjectAgentInstance>>;
  readonly onPreviewConsolidation: () => Promise<CmmdProjectAgentMemberConsolidationPreview>;
  readonly onApplyConsolidation: (
    expectedPlanHash: string,
  ) => Promise<CmmdProjectAgentMemberConsolidationResult>;
}

export function ProjectAgentMembers(props: ProjectAgentMembersProps) {
  const [query, setQuery] = useState("");
  const [helpOpen, setHelpOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [instances, setInstances] = useState<ReadonlyArray<CmmdProjectAgentInstance>>([]);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [showRetired, setShowRetired] = useState(false);
  const historyRequestRef = useRef(0);
  const taskRows = flattenTaskTree(props.tasks);
  const activeMembers = props.members.filter((member) => member.lifecycleStatus === "active");
  const retiredCount = props.members.length - activeMembers.length;
  const visibleMembers = showRetired ? props.members : activeMembers;
  const filtered = filterAgentMembers(visibleMembers, taskRows, query);
  const selected = props.members.find((member) => member.id === selectedId) ?? null;

  const openDetails = async (member: CmmdProjectAgentMember) => {
    const requestId = ++historyRequestRef.current;
    setSelectedId(member.id);
    setHistoryLoading(true);
    setHistoryError(null);
    try {
      const nextInstances = await props.onListInstances(member.id);
      if (historyRequestRef.current !== requestId) return;
      setInstances(nextInstances);
    } catch (cause) {
      if (historyRequestRef.current !== requestId) return;
      setInstances([]);
      setHistoryError(cause instanceof Error ? cause.message : "Instance history is unavailable.");
    } finally {
      if (historyRequestRef.current === requestId) setHistoryLoading(false);
    }
  };

  return (
    <section aria-labelledby="project-agent-members-heading" className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="flex items-center gap-2">
            <h3
              id="project-agent-members-heading"
              className="text-xs font-semibold uppercase tracking-wide text-muted-foreground"
            >
              Agent Members
            </h3>
            <span className="text-xs tabular-nums text-muted-foreground/70">
              {visibleMembers.length}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label="How Agent Members work"
            aria-expanded={helpOpen}
            onClick={() => setHelpOpen((open) => !open)}
          >
            <HelpCircleIcon className="size-4" aria-hidden />
          </Button>
          {retiredCount > 0 ? (
            <Button
              variant="ghost"
              size="sm"
              disabled={props.loading || props.error !== null}
              onClick={() => setShowRetired((visible) => !visible)}
            >
              {showRetired ? "Hide retired" : `Show retired (${retiredCount})`}
            </Button>
          ) : null}
          <Button
            size="sm"
            disabled={props.loading || props.error !== null}
            onClick={() => setCreateOpen(true)}
          >
            <PlusIcon className="size-3.5" aria-hidden />
            Add agent
          </Button>
        </div>
      </div>
      {helpOpen ? (
        <div className="rounded-lg border border-border bg-muted/40 px-3 py-2 text-xs leading-5 text-muted-foreground">
          Agent Members are reusable project identities with stable IDs and scoped authority.
          Provider credentials remain in the agent client; each run is traced as an instance.
        </div>
      ) : null}

      <AgentMemberConsolidationPanel
        members={props.members}
        disabled={props.loading || props.error !== null}
        onPreview={props.onPreviewConsolidation}
        onApply={props.onApplyConsolidation}
      />

      <div className="overflow-hidden rounded-lg border border-border bg-card">
        <label className="relative block border-b border-border p-2">
          <SearchIcon
            className="pointer-events-none absolute left-4 top-4 size-4 text-muted-foreground"
            aria-hidden
          />
          <span className="sr-only">Search Agent Members</span>
          <Input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search agents by name, role, or provider"
            className="h-8 border-0 bg-muted/60 pl-8 shadow-none"
          />
        </label>
        {props.loading ? (
          <p
            role="status"
            className="m-3 rounded-md border border-dashed p-4 text-sm text-muted-foreground"
          >
            Loading Agent Members…
          </p>
        ) : props.error ? (
          <p
            role="alert"
            className="m-3 rounded-md border border-destructive-border p-3 text-sm text-destructive"
          >
            Agent Members could not be loaded: {props.error}
          </p>
        ) : filtered.length === 0 ? (
          <p className="m-3 rounded-md border border-dashed p-4 text-sm text-muted-foreground">
            {visibleMembers.length === 0
              ? "No reusable Agent Members yet. Add a named seat before assigning agent work."
              : "No Agent Members match this search."}
          </p>
        ) : (
          <>
            <div className="hidden grid-cols-[minmax(16rem,1.4fr)_minmax(8rem,0.6fr)_minmax(9rem,0.75fr)_7rem] gap-5 border-b border-border bg-muted/40 px-4 py-2 text-left text-[10px] font-medium uppercase tracking-wide text-muted-foreground md:grid">
              <span>Agent</span>
              <span>Provider</span>
              <span>Role</span>
              <span>Status</span>
            </div>
            <ul className="divide-y divide-border">
              {filtered.map((member) => {
                return (
                  <li key={member.id}>
                    <button
                      type="button"
                      onClick={() => void openDetails(member)}
                      aria-pressed={selectedId === member.id}
                      className={cn(
                        "grid min-h-14 w-full grid-cols-1 items-center gap-2 px-4 py-2.5 text-left transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring md:grid-cols-[minmax(16rem,1.4fr)_minmax(8rem,0.6fr)_minmax(9rem,0.75fr)_7rem] md:gap-5",
                        selectedId === member.id ? "bg-accent" : null,
                      )}
                    >
                      <span className="flex min-w-0 items-center gap-3">
                        <AgentMemberAvatar member={member} />
                        <span className="min-w-0">
                          <span className="truncate text-sm font-semibold">
                            {member.displayName}
                          </span>
                          <span className="mt-1 block truncate font-mono text-[11px] text-muted-foreground">
                            {member.agentId}
                          </span>
                        </span>
                      </span>
                      <span className="hidden md:block">
                        <AgentKindBadge kind={member.agentKind} />
                      </span>
                      <span className="hidden truncate text-xs text-muted-foreground md:block">
                        {formatTeamRole(member.primaryRole)}
                      </span>
                      <span className="hidden md:block">
                        <LifecycleBadge status={member.lifecycleStatus} />
                      </span>
                      <span className="flex items-center gap-2 text-xs text-muted-foreground md:hidden">
                        <AgentKindBadge kind={member.agentKind} />
                        <span>{formatTeamRole(member.primaryRole)}</span>
                        <span aria-hidden>·</span>
                        <LifecycleBadge status={member.lifecycleStatus} />
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </div>

      <CreateAgentMemberDialog
        key={props.projectId}
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreate={props.onCreate}
      />
      <AgentMemberDetails
        key={selected?.id ?? "closed"}
        member={selected}
        tasks={taskRows}
        instances={instances}
        events={props.events.filter((event) => event.agentSeatId === selected?.id)}
        eventsLoading={props.eventsLoading}
        eventsError={props.eventsError}
        historyLoading={historyLoading}
        historyError={historyError}
        onClose={() => {
          historyRequestRef.current += 1;
          setSelectedId(null);
        }}
        onRename={props.onRename}
        onRetire={props.onRetire}
        onUpdateAvatar={props.onUpdateAvatar}
      />
    </section>
  );
}

function LifecycleBadge({
  status,
}: {
  readonly status: CmmdProjectAgentMember["lifecycleStatus"];
}) {
  return (
    <span className="inline-flex items-center gap-1.5 text-[11px] capitalize text-muted-foreground">
      <span
        className={cn(
          "size-1.5 rounded-full",
          status === "active" ? "bg-success" : "bg-muted-foreground/60",
        )}
        aria-hidden
      />
      {status}
    </span>
  );
}

function assignedTasks(memberId: number, tasks: readonly CmmdBoardTask[]) {
  return tasks.filter(
    (task) =>
      task.assignment?.kind === "agent_member" && task.assignment.agentMemberId === memberId,
  );
}

export function filterAgentMembers(
  members: readonly CmmdProjectAgentMember[],
  tasks: readonly CmmdBoardTask[],
  query: string,
): readonly CmmdProjectAgentMember[] {
  const needle = query.trim().toLowerCase();
  if (!needle) return members;
  return members.filter((member) =>
    [
      member.displayName,
      member.agentId,
      member.agentKind,
      member.primaryRole,
      ...member.capabilities,
      member.lifecycleStatus,
      ...assignedTasks(member.id, tasks).flatMap((task) => [task.title, task.status]),
    ]
      .join(" ")
      .toLowerCase()
      .includes(needle),
  );
}
